import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, EventEmitter, inject, input, Input, Output, ViewChild } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { RouterModule } from '@angular/router'
import { SubscriptionPlanType } from '@ftr/external-organization-contracts'
import { DropdownMenuComponent, IconComponent, MenuItemGroup, MenuItemLink } from '@ftr/foundation'
import { DropdownMenuLegacyComponent } from '@ftr/menus'
import { AppPaths } from '@ftr/routing-paths'
import { UserAvatarComponent, UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { map } from 'rxjs'
import { SideNavDisplayMode } from '../../navigation'

export const USER_AND_ORG_MENU_ITEMS = [
  { label: 'Browse organizations', path: [`/${AppPaths.Organizations}`], icon: 'Grid' },
  {
    label: 'Create organization',
    path: [`/${AppPaths.SelectPlanTier}`],
    queryParams: { planType: SubscriptionPlanType.Organization, countryCode: 'US' },
    icon: 'Add',
  },
] satisfies MenuItemLink[]

export function individualMenuItem(user: { givenName: string; familyName: string }): MenuItemLink {
  return {
    label: `${user.givenName} ${user.familyName}`,
    path: [`/${AppPaths.Individual}`],
    icon: 'Person',
  }
}

@Component({
  selector: 'ftr-user-and-org-dropdown',
  templateUrl: './user-and-org-dropdown.component.html',
  styleUrls: ['./user-and-org-dropdown.component.css'],
  host: {
    '[attr.theme]': 'theme()',
  },
  imports: [DropdownMenuComponent, UserAvatarComponent, IconComponent, RouterModule, NgTemplateOutlet],
})
export class UserAndOrgDropdownComponent {
  private readonly store = inject(Store)

  @Input() collapsed: boolean = false
  @Input() displayMode: SideNavDisplayMode = 'DEFAULT'
  theme = input<'light' | 'dark'>('dark')
  @Output() dropdownOpened = new EventEmitter<void>()

  @ViewChild(DropdownMenuLegacyComponent) protected dropdown: DropdownMenuLegacyComponent

  protected readonly currentUser = toSignal(
    this.store.select(UserState.user).pipe(map(user => user.unwrapOr(undefined))),
  )
  protected readonly appPaths = AppPaths

  protected readonly menuItems = computed<MenuItemGroup[]>(() => {
    const user = this.currentUser()
    return [
      ...(user ? [{ key: 'individual', items: [individualMenuItem(user)] }] : []),
      { key: 'organizations', items: [...USER_AND_ORG_MENU_ITEMS] },
    ]
  })

  protected readonly componentSelector = 'ftr-user-and-org-dropdown'

  toggle(): void {
    this.dropdown.toggle()
  }
}
